export type HelpOrderStatus =
  | "awaiting_confirmation"
  | "en_route"
  | "on_site"
  | "in_progress"
  | "completed"
  | "cancelled";

export type HelpProvider = "internal" | "lat";

export type HelpPaymentStatus = "pending" | "success" | "refunded";

export type HelpPricingMode = "fixed" | "per_unit" | "flexible";

export type HelpServiceKind = "tow" | "jump_start" | "fuel";

export type HelpEventType =
  | "system"
  | "status"
  | "comment"
  | "notify"
  | "executor";

export interface HelpPoint {
  city: string;
  address: string;
  lat: number;
  lon: number;
  note: string;
}

export interface HelpExecutorProfile {
  uuid: string;
  name: string;
  phone: string;
  vehicleModel: string;
  vehiclePlate: string;
  experienceYears: number;
  company: string;
  enabled: boolean;
}

export interface HelpExecutor {
  source: "catalog" | "custom";
  catalogUuid: string | null;
  name: string;
  phone: string;
  vehicleModel: string;
  vehiclePlate: string;
  experienceYears: number;
  company: string;
  lat: number | null;
  lon: number | null;
  etaMinutes: number | null;
}

export function emptyExecutor(
  extras?: Pick<HelpExecutor, "lat" | "lon" | "etaMinutes">,
): HelpExecutor {
  return {
    source: "custom",
    catalogUuid: null,
    name: "",
    phone: "",
    vehicleModel: "",
    vehiclePlate: "",
    experienceYears: 0,
    company: "",
    lat: extras?.lat ?? null,
    lon: extras?.lon ?? null,
    etaMinutes: extras?.etaMinutes ?? null,
  };
}

export function blankExecutorAssignment(): HelpExecutor {
  return {
    ...emptyExecutor(),
    source: "catalog",
  };
}

export function executorFromProfile(
  profile: HelpExecutorProfile,
  extras?: Pick<HelpExecutor, "lat" | "lon" | "etaMinutes">,
): HelpExecutor {
  return {
    source: "catalog",
    catalogUuid: profile.uuid,
    name: profile.name,
    phone: profile.phone,
    vehicleModel: profile.vehicleModel,
    vehiclePlate: profile.vehiclePlate,
    experienceYears: profile.experienceYears,
    company: profile.company,
    lat: extras?.lat ?? null,
    lon: extras?.lon ?? null,
    etaMinutes: extras?.etaMinutes ?? null,
  };
}

export interface HelpEvent {
  id: string;
  type: HelpEventType;
  at: string;
  title: string;
  detail?: string;
}

export interface HelpLineItem {
  id: string;
  source: "catalog" | "custom";
  serviceUuid: string | null;
  code: string;
  title: string;
  quantity: number;
  unitPrice: number;
  pricingMode: HelpPricingMode;
}

export interface HelpOrderDetail {
  uuid: string;
  createdAt: string;
  updatedAt: string;
  status: HelpOrderStatus;
  kind: HelpServiceKind;
  serviceTitle: string;
  clientName: string;
  clientPhone: string;
  clientUuid: string;
  carUuid: string | null;
  vehicle: {
    makeModel: string;
    regNumber: string;
    vin: string | null;
    year: string | null;
  };
  pickup: HelpPoint;
  dropoff: HelpPoint | null;
  comment: string;
  blockedWheels: number;
  hardToAccess: boolean;
  items: HelpLineItem[];
  prepaidAmount: number;
  prepaidStatus: HelpPaymentStatus;
  extraAmount: number;
  extraPaymentStatus: HelpPaymentStatus;
  totalAmount: number;
  provider: HelpProvider;
  latOrderId: string | null;
  latGuid: string | null;
  latShortLink: string | null;
  latStatusRaw: string | null;
  latAmount: number | null;
  latTest: boolean;
  latSentAt: string | null;
  latSyncedAt: string | null;
  latLastError: string | null;
  executor: HelpExecutor | null;
  events: HelpEvent[];
}

export interface HelpService {
  uuid: string;
  kind: HelpServiceKind | "addon";
  code: string;
  title: string;
  description: string;
  unitPrice: number;
  pricingMode: HelpPricingMode;
  unitLabel: string;
  enabled: boolean;
  comingSoon: boolean;
  providerServiceId: string;
}

export function lineItemAmount(item: HelpLineItem): number {
  return Math.round(item.quantity * item.unitPrice);
}

export function orderTotal(order: Pick<HelpOrderDetail, "items">): number {
  return order.items.reduce((sum, item) => sum + lineItemAmount(item), 0);
}

export function extraOnSiteAmount(
  order: Pick<HelpOrderDetail, "items" | "prepaidAmount">,
): number {
  return Math.max(0, orderTotal(order) - order.prepaidAmount);
}
